import { Input, Modal, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { trackerManager } from '@hero/clients';
import {
  FORM_ERROR_MESSAGES,
  MAX_CONTACT_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MIN_LENGTH,
  TRACKER_EVENT
} from '@hero/constants';
import { useRecogRecipientsBulkUpsert } from '@hero/services';
import { clearObject } from '@hero/utils';

import * as S from './style';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .max(MAX_EMAIL_LENGTH, FORM_ERROR_MESSAGES.max50Char)
    .email(FORM_ERROR_MESSAGES.invalidEmail)
    .required(FORM_ERROR_MESSAGES.emailRequired),
  name: yup.string().min(MIN_LENGTH).max(MAX_CONTACT_NAME_LENGTH, FORM_ERROR_MESSAGES.max100Char)
});

export function AddRecipientModal({ isOpen, onRequestClose, recognitionCode }) {
  const { t } = useTranslation();
  const { mutateAsync: recipientBulkUpsert, isLoading: isRecipientBulkUpsertLoading } =
    useRecogRecipientsBulkUpsert();

  const sendNewContactsToApi = async contact => {
    await recipientBulkUpsert({
      params: {
        code: recognitionCode
      },
      body: {
        recipients: [
          clearObject({
            email: contact?.email,
            firstName: contact?.name
          })
        ]
      }
    });

    trackerManager.emit(TRACKER_EVENT.contactAdded, {
      label: t('uploadSpreadsheet')
    });
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      name: ''
    },
    validationSchema,
    onSubmit: async data => {
      await sendNewContactsToApi(data);

      formik.resetForm();
      onRequestClose();
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('addRecipient')}
      size="sm"
      primaryButtonProps={{
        children: t('save'),
        onClick: () => formik.submitForm(),
        loading: isRecipientBulkUpsertLoading
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
    >
      <S.Wrapper>
        <S.FormItem>
          <Typography variant="body2Bold">{t('email')}</Typography>
          <Input
            name="email"
            variant="outlined"
            title={t('email')}
            onChange={e => {
              formik.setFieldValue('email', e.target.value);
            }}
            value={formik?.values?.email}
            inputProps={{
              maxLength: MAX_EMAIL_LENGTH
            }}
            error={formik?.errors?.email}
            helperText={formik?.errors?.email}
            block
          />
        </S.FormItem>
        <S.FormItem>
          <Typography variant="body2Bold">{`${t('name')} (${t('optional').toLocaleLowerCase()})`}</Typography>
          <Input
            name="name"
            variant="outlined"
            onChange={e => {
              formik.setFieldValue('name', e.target.value);
            }}
            value={formik?.values?.name}
            inputProps={{
              maxLength: MAX_CONTACT_NAME_LENGTH
            }}
            error={formik?.errors?.name}
            helperText={formik?.errors?.name}
            block
          />
        </S.FormItem>
      </S.Wrapper>
    </Modal>
  );
}

AddRecipientModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onAsyncSubmit: PropTypes.func.isRequired,
  recognitionCode: PropTypes.string.isRequired
};
